"use client";

import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from "react";
import { flushSync } from "react-dom";
import type { Locale } from "@/kernel/locale";
import { BRAND_LOGO_WHITE, Icon } from "@/shared/ui";
import { isEmailAddress } from "../../domain/email";
import { onOpenSignIn } from "./dialog-events";
import { signIn } from "./gateway";

export type SignInDialogCopy = {
  close: string;
  logoAlt: string;
  title: string;
  subtitle: string;
  email: string;
  password: string;
  showPassword: string;
  hidePassword: string;
  submit: string;
  submitting: string;
  forgot: string;
  invalidCredentials: string;
  unavailable: string;
  emailInvalid: string;
  passwordRequired: string;
  notConnected: string;
  connect: string;
  recovery: { title: string; text: string; back: string };
};

/** undefined: not checked yet; null: valid; a string: the message shown. */
type FieldState = string | null | undefined;

type Failure = "invalid_credentials" | "unavailable" | null;

const invalid = (state: FieldState) => (state === undefined ? undefined : state !== null);

// A protected page sends a signed-out visitor to the homepage with ?signin
// (and the cabinet address in ?next); the dialog then opens by itself.
const OPEN_PARAM = "signin";
const NEXT_PARAM = "next";

/**
 * The sign-in dialog of the prototype: email and password with a visibility
 * toggle, and a "forgot your password?" view that explains how access is
 * restored (no reset email exists yet). A successful sign-in opens the cabinet.
 */
export function SignInDialog({ copy, locale }: { copy: SignInDialogCopy; locale: Locale }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const toConnect = useRef(false);
  const next = useRef<string | null>(null);
  const loginEmail = useRef<HTMLInputElement>(null);
  const password = useRef<HTMLInputElement>(null);
  const backLink = useRef<HTMLButtonElement>(null);
  const loginForm = useRef<HTMLFormElement>(null);

  const [view, setView] = useState<"login" | "recovery">("login");
  const [emailState, setEmailState] = useState<FieldState>(undefined);
  const [passwordState, setPasswordState] = useState<FieldState>(undefined);
  const [failure, setFailure] = useState<Failure>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [passwordShown, setPasswordShown] = useState(false);

  // The address's request to open the dialog (?signin), read once: React's
  // development double-mount keeps refs, so the second run still opens it.
  const addressRequest = useRef<{ next: string | null } | null | undefined>(undefined);

  useEffect(() => {
    const open = (button: HTMLElement | null) => {
      opener.current = button;
      toConnect.current = false;
      loginForm.current?.reset();
      flushSync(() => {
        setView("login");
        setEmailState(undefined);
        setPasswordState(undefined);
        setFailure(null);
        setPasswordShown(false);
      });
      if (!dialog.current?.open) dialog.current?.showModal();
      loginEmail.current?.focus();
    };
    const unsubscribe = onOpenSignIn((button) => {
      next.current = null;
      open(button);
    });

    if (addressRequest.current === undefined) {
      const url = new URL(window.location.href);
      addressRequest.current = url.searchParams.has(OPEN_PARAM) ? { next: url.searchParams.get(NEXT_PARAM) } : null;
      if (addressRequest.current) {
        url.searchParams.delete(OPEN_PARAM);
        url.searchParams.delete(NEXT_PARAM);
        window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
      }
    }
    const request = addressRequest.current;
    // After this effect: React may not flush synchronously while it commits.
    const timer = request
      ? window.setTimeout(() => {
          addressRequest.current = null;
          next.current = request.next;
          open(null);
        }, 0)
      : undefined;
    return () => {
      window.clearTimeout(timer);
      unsubscribe();
    };
  }, []);

  const close = () => dialog.current?.close();

  const onClose = () => {
    if (toConnect.current) return;
    const button = opener.current;
    // The opener may sit in the mobile menu, which closed when the dialog opened.
    const back = button && button.offsetParent ? button : document.querySelector<HTMLElement>("[data-focus-fallback]");
    back?.focus({ preventScroll: true });
  };

  const onBackdrop = (event: MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialog.current) close();
  };

  const go = (target: "login" | "recovery") => {
    flushSync(() => setView(target));
    if (target === "recovery") backLink.current?.focus();
    else loginEmail.current?.focus();
  };

  const onSignIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (signingIn) return;
    setFailure(null);
    const emailOk = isEmailAddress(loginEmail.current?.value ?? "");
    const passwordOk = Boolean(password.current?.value);
    setEmailState(emailOk ? null : copy.emailInvalid);
    setPasswordState(passwordOk ? null : copy.passwordRequired);
    if (!emailOk) {
      loginEmail.current?.focus();
      return;
    }
    if (!passwordOk) {
      password.current?.focus();
      return;
    }
    setSigningIn(true);
    const result = await signIn({
      email: loginEmail.current?.value.trim() ?? "",
      password: password.current?.value ?? "",
      next: next.current,
      locale,
    });
    if (result.ok) {
      // The busy state stays while the cabinet opens.
      window.location.assign(result.location);
      return;
    }
    flushSync(() => {
      setSigningIn(false);
      setFailure(result.code);
    });
    password.current?.select();
    password.current?.focus();
  };

  const failureText = failure === "invalid_credentials" ? copy.invalidCredentials : failure === "unavailable" ? copy.unavailable : null;

  return (
    <dialog className="modal" id="login" ref={dialog} aria-labelledby={view === "login" ? "loginTitle" : "recoveryTitle"} onClose={onClose} onClick={onBackdrop}>
      <div className="modal-card">
        <button className="modal-x" type="button" aria-label={copy.close} onClick={close}><Icon name="x" className="ic" /></button>
        {/* eslint-disable-next-line @next/next/no-img-element -- a fixed-size SVG wordmark */}
        <img className="modal-logo" src={BRAND_LOGO_WHITE} width={105} height={28} alt={copy.logoAlt} />

        <div data-view="login" hidden={view !== "login"}>
          <h2 className="modal-title" id="loginTitle">{copy.title}</h2>
          <p className="modal-sub">{copy.subtitle}</p>
          {/* POST, so a submission without the handler never puts the credentials in the address. */}
          <form className="mform" id="loginForm" ref={loginForm} method="post" noValidate onSubmit={onSignIn}>
            <div className="f">
              <label htmlFor="l-email">{copy.email}</label>
              <input id="l-email" ref={loginEmail} name="email" type="email" autoComplete="username" autoCapitalize="off" spellCheck={false} maxLength={320} required aria-describedby="le-email" aria-invalid={invalid(emailState)} />
              <span className="err" id="le-email" aria-live="polite">{emailState ?? ""}</span>
            </div>
            <div className="f">
              <label htmlFor="l-pass">{copy.password}</label>
              <div className="pw">
                <input id="l-pass" ref={password} name="password" type={passwordShown ? "text" : "password"} autoComplete="current-password" maxLength={256} required aria-describedby="le-pass" aria-invalid={invalid(passwordState)} />
                <button
                  className="pw-eye"
                  type="button"
                  aria-pressed={passwordShown}
                  aria-label={passwordShown ? copy.hidePassword : copy.showPassword}
                  aria-controls="l-pass"
                  onClick={() => setPasswordShown((shown) => !shown)}
                >
                  <Icon name="eye" className="ic i-show" />
                  <Icon name="eyeoff" className="ic i-hide" />
                </button>
              </div>
              <span className="err" id="le-pass" aria-live="polite">{passwordState ?? ""}</span>
            </div>
            <div className="form-err" id="loginErr" role="alert">{failureText ? <><Icon name="alert" className="ic" />{failureText}</> : null}</div>
            <button className="btn btn-primary wide" type="submit" aria-busy={signingIn} disabled={signingIn}>
              <span className="spin" aria-hidden="true" />
              <span className="lbl">{signingIn ? copy.submitting : copy.submit}</span>
            </button>
            <button className="linkish" type="button" onClick={() => go("recovery")}>{copy.forgot}</button>
          </form>
        </div>

        <div data-view="recovery" hidden={view !== "recovery"}>
          <h2 className="modal-title" id="recoveryTitle">{copy.recovery.title}</h2>
          <div className="reset-done recovery-note" id="recoveryNote">
            <Icon name="shield" className="ic" />
            <p>{copy.recovery.text}</p>
          </div>
          <button className="linkish modal-back" type="button" ref={backLink} onClick={() => go("login")}>{copy.recovery.back}</button>
        </div>

        <p className="modal-foot">
          {copy.notConnected} <a href="#connect" onClick={() => { toConnect.current = true; close(); }}>{copy.connect}</a>
        </p>
      </div>
    </dialog>
  );
}
